import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, Divider, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  AttachMoney as DinheiroIcon,
  CreditCard as CartaoIcon,
  QrCode as PixIcon,
  Receipt as BoletoIcon,
  MoreHoriz as OutroIcon,
} from '@mui/icons-material';
import { useTheme as useAppTheme } from '../context/ThemeContext';
import maquinaCartaoService from '../services/maquinaCartaoService';
import { BANDEIRAS, acharTaxa, parcelasDisponiveis } from '../utils/taxaCartao';
import BandeiraIcone from './BandeiraIcone';

const FORMAS = [
  { value: 'dinheiro', label: 'Dinheiro', Icon: DinheiroIcon },
  { value: 'pix',      label: 'Pix',      Icon: PixIcon      },
  { value: 'cartao',   label: 'Crédito',  Icon: CartaoIcon   },
  { value: 'debito',   label: 'Débito',   Icon: CartaoIcon   },
  { value: 'boleto',   label: 'Boleto',   Icon: BoletoIcon   },
  { value: 'outro',    label: 'Outro',    Icon: OutroIcon    },
];

function formatarReais(valor) {
  if (!valor && valor !== 0) return 'R$ 0,00';
  const num = parseFloat(valor);
  if (isNaN(num)) return 'R$ 0,00';
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function mascararReais(raw) {
  const digits = String(raw).replace(/\D/g, '');
  if (!digits) return '';
  const num = parseInt(digits, 10) / 100;
  const [inteiro, decimal] = num.toFixed(2).split('.');
  const inteiroFormatado = inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `R$ ${inteiroFormatado},${decimal}`;
}

function parseMascara(str) {
  const digits = String(str).replace(/\D/g, '');
  return digits ? parseInt(digits, 10) / 100 : 0;
}

// valorTotal: valor total do orçamento/procedimento
// valorJaPago: soma do que já foi lançado pra esse mesmo orçamento (entradas anteriores)
const ModalPagamento = ({ open, onClose, valorTotal, valorJaPago = 0, onSave, tipoUsuario = 'PF' }) => {
  const { isDarkMode } = useAppTheme();
  const isHibrido = String(tipoUsuario).toUpperCase() === 'HIBRIDO';
  const tipoFixo = isHibrido ? null : String(tipoUsuario).toUpperCase();

  const saldoRestante = Math.max(0, Math.round(((valorTotal || 0) - valorJaPago) * 100) / 100);

  const [forma, setForma] = useState('dinheiro');
  const [valor, setValor] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [tipoPessoa, setTipoPessoa] = useState(tipoFixo || 'PF');

  // Máquina de cartão
  const [maquinas, setMaquinas] = useState([]);
  const [maquinaId, setMaquinaId] = useState('');
  const [parcelas, setParcelas] = useState('');
  const [bandeira, setBandeira] = useState('');
  const [antecipado, setAntecipado] = useState(false);
  const [taxaResponsavel, setTaxaResponsavel] = useState('clinica');

  useEffect(() => {
    if (open) {
      maquinaCartaoService.listar().then(lista => setMaquinas(Array.isArray(lista) ? lista : [])).catch(() => {});
    }
  }, [open]);

  React.useEffect(() => {
    const base = saldoRestante > 0 ? saldoRestante : valorTotal;
    if (base) {
      const digits = Math.round(base * 100);
      setValor(mascararReais(String(digits)));
    } else {
      setValor('');
    }
    setForma('dinheiro');
    setObservacoes('');
    setTipoPessoa(tipoFixo || 'PF');
    setMaquinaId('');
    setParcelas('');
    setBandeira('');
    setAntecipado(false);
    setTaxaResponsavel('clinica');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valorTotal, valorJaPago, open]);

  const maquinaSelecionada = maquinas.find(m => m.id === maquinaId) || null;
  const ehDebito = forma === 'debito';
  const taxaSelecionada = maquinaSelecionada && (ehDebito || parcelas)
    ? acharTaxa(maquinaSelecionada, { modalidade: ehDebito ? 'debito' : 'credito', bandeira, parcelas: ehDebito ? 1 : parcelas })
    : null;
  const percentualPreview = antecipado && taxaSelecionada?.taxaAntecipacaoPercentual != null
    ? taxaSelecionada.taxaAntecipacaoPercentual
    : taxaSelecionada?.taxaPercentual;
  const valorTaxaPreview = taxaSelecionada && percentualPreview != null
    ? parseMascara(valor) * (parseFloat(percentualPreview) / 100)
    : null;

  const valorNumerico = parseMascara(valor);
  const saldoAposEstePagamento = Math.max(0, Math.round((saldoRestante - valorNumerico) * 100) / 100);

  const handleSave = () => {
    const num = parseMascara(valor);
    if (!num || num <= 0) {
      alert('Informe o valor do pagamento!');
      return;
    }
    const payload = { forma, valor: num, observacoes, tipoPessoa: isHibrido ? tipoPessoa : tipoFixo };
    if (((forma === 'cartao' && parcelas) || ehDebito) && maquinaId) {
      payload.maquinaCartaoId = maquinaId;
      payload.parcelasCartao = ehDebito ? 1 : parcelas;
      payload.bandeiraCartao = bandeira;
      payload.cartaoAntecipado = ehDebito ? false : antecipado;
      payload.taxaCartaoResponsavel = taxaResponsavel;
    }
    onSave(payload);
    onClose();
  };

  const paperSx = isDarkMode
    ? { background: 'rgba(30,41,59,0.97)', border: '1px solid rgba(249,115,22,0.25)', borderRadius: '16px' }
    : { background: '#FFFAF5', border: '1px solid rgba(249,115,22,0.2)', borderRadius: '16px' };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: paperSx }}>

      {/* Header */}
      <DialogTitle sx={{ pb: 1, pt: 2.5, px: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={700} sx={{ borderLeft: '4px solid #F97316', pl: 1.5, lineHeight: 1.2 }}>
            Registrar Pagamento
          </Typography>
          <Box
            onClick={onClose}
            sx={{ cursor: 'pointer', color: 'text.secondary', '&:hover': { color: '#F97316' }, transition: 'color 0.2s' }}
          >
            <CloseIcon />
          </Box>
        </Box>
      </DialogTitle>

      <Divider sx={{ borderColor: 'rgba(249,115,22,0.15)' }} />

      <DialogContent sx={{ px: 3, py: 2.5 }}>

        {/* Valor em destaque */}
        {valorTotal > 0 && (
          <Box sx={{
            borderRadius: '10px', mb: 2.5, overflow: 'hidden',
            border: '1px solid rgba(249,115,22,0.2)',
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '10px 16px', bgcolor: 'rgba(249,115,22,0.08)' }}>
              <Typography variant="body2" fontWeight={600} color="text.secondary">Valor do Orçamento</Typography>
              <Typography variant="body1" fontWeight={800} sx={{ color: '#F97316' }}>{formatarReais(valorTotal)}</Typography>
            </Box>
            {valorJaPago > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '8px 16px' }}>
                <Typography variant="caption" color="text.secondary">Já pago anteriormente</Typography>
                <Typography variant="caption" fontWeight={700} color="success.main">{formatarReais(valorJaPago)}</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '8px 16px', borderTop: valorJaPago > 0 ? '1px dashed rgba(249,115,22,0.2)' : 'none' }}>
              <Typography variant="caption" fontWeight={700}>Saldo em aberto</Typography>
              <Typography variant="caption" fontWeight={800}>{formatarReais(saldoRestante)}</Typography>
            </Box>
          </Box>
        )}

        {/* Tipo de Pessoa — só híbrido vê */}
        {isHibrido && (
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="caption" sx={{ color: '#F97316', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, display: 'block' }}>
              Tipo de Lançamento
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {[{ value: 'PF', label: 'Pessoa Física' }, { value: 'PJ', label: 'Pessoa Jurídica' }].map(opt => {
                const ativo = tipoPessoa === opt.value;
                return (
                  <Box
                    key={opt.value}
                    onClick={() => setTipoPessoa(opt.value)}
                    sx={{
                      flex: 1, py: 1, textAlign: 'center', borderRadius: '10px', cursor: 'pointer',
                      border: `1.5px solid ${ativo ? '#F97316' : 'rgba(249,115,22,0.2)'}`,
                      bgcolor: ativo ? 'rgba(249,115,22,0.09)' : (isDarkMode ? 'rgba(255,255,255,0.03)' : '#fff'),
                      color: ativo ? '#F97316' : 'text.secondary',
                      fontWeight: ativo ? 700 : 400,
                      fontSize: '0.82rem',
                      transition: 'all 0.15s',
                      userSelect: 'none',
                      '&:hover': { borderColor: '#F97316', color: '#F97316' }
                    }}
                  >
                    {opt.label}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {/* Tipo fixo — exibe badge informativo para PF/PJ */}
        {!isHibrido && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: '8px 12px', borderRadius: '8px', bgcolor: 'rgba(249,115,22,0.06)', border: '1px solid rgba(249,115,22,0.15)' }}>
            <Typography variant="caption" color="text.secondary">Lançamento como</Typography>
            <Box sx={{
              px: 1.5, py: 0.25, borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700,
              bgcolor: tipoFixo === 'PJ' ? 'rgba(249,115,22,0.12)' : 'rgba(37,99,235,0.1)',
              color: tipoFixo === 'PJ' ? '#EA580C' : '#2563EB',
              border: `1px solid ${tipoFixo === 'PJ' ? 'rgba(249,115,22,0.3)' : 'rgba(37,99,235,0.3)'}`,
            }}>
              {tipoFixo === 'PJ' ? 'Pessoa Jurídica' : 'Pessoa Física'}
            </Box>
          </Box>
        )}

        {/* Forma de pagamento */}
        <Typography variant="caption" sx={{ color: '#F97316', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, display: 'block' }}>
          Forma de Pagamento
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 2.5 }}>
          {FORMAS.map(fp => {
            const ativo = forma === fp.value;
            return (
              <Box
                key={fp.value}
                onClick={() => setForma(fp.value)}
                sx={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  gap: 0.5, py: 1.5, px: 0.5, borderRadius: '10px', cursor: 'pointer',
                  border: `1.5px solid ${ativo ? '#F97316' : 'rgba(249,115,22,0.15)'}`,
                  bgcolor: ativo ? 'rgba(249,115,22,0.08)' : (isDarkMode ? 'rgba(255,255,255,0.03)' : '#fff'),
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: '#F97316', bgcolor: 'rgba(249,115,22,0.05)' }
                }}
              >
                <fp.Icon sx={{ fontSize: '1.3rem', color: ativo ? '#F97316' : 'text.disabled' }} />
                <Typography variant="caption" fontWeight={ativo ? 700 : 400} sx={{ color: ativo ? '#F97316' : 'text.secondary', fontSize: '0.68rem', textAlign: 'center' }}>
                  {fp.label}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Máquina de cartão — só aparece com Crédito ou Débito */}
        {(forma === 'cartao' || ehDebito) && (
          <Box sx={{
            border: '1px dashed rgba(249,115,22,0.4)', borderRadius: '10px', p: 1.5, mb: 2.5,
            bgcolor: isDarkMode ? 'rgba(249,115,22,0.06)' : 'rgba(249,115,22,0.04)',
          }}>
            {maquinas.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                Nenhuma máquina cadastrada — cadastre em Faturamento → Gerenciar máquinas pra calcular a taxa automaticamente.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Máquina</InputLabel>
                    <Select value={maquinaId} label="Máquina" onChange={(e) => { setMaquinaId(e.target.value); setParcelas(''); setBandeira(''); }}>
                      <MenuItem value=""><em>Nenhuma</em></MenuItem>
                      {maquinas.map(m => <MenuItem key={m.id} value={m.id}>{m.nome}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small" disabled={!maquinaSelecionada}>
                    <InputLabel>Bandeira</InputLabel>
                    <Select
                      value={bandeira} label="Bandeira"
                      onChange={(e) => {
                        const nova = e.target.value;
                        const aindaExiste = parcelasDisponiveis(maquinaSelecionada, nova).some(t => String(t.parcelas) === String(parcelas));
                        setBandeira(nova);
                        if (!aindaExiste) setParcelas('');
                      }}
                    >
                      <MenuItem value=""><em>Não informar (taxa padrão)</em></MenuItem>
                      {BANDEIRAS.map(b => (
                        <MenuItem key={b} value={b} sx={{ gap: 1.25 }}><BandeiraIcone bandeira={b} largura={28} />{b}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                {!ehDebito && (
                  <FormControl fullWidth size="small" disabled={!maquinaSelecionada}>
                    <InputLabel>Parcelas</InputLabel>
                    <Select value={parcelas} label="Parcelas" onChange={(e) => setParcelas(e.target.value)}>
                      {parcelasDisponiveis(maquinaSelecionada, bandeira).map(t => (
                        <MenuItem key={t.parcelas} value={t.parcelas}>{t.parcelas}x ({t.taxaPercentual}%)</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {(ehDebito ? maquinaId : parcelas) && (
                  <>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Taxa fica com</InputLabel>
                        <Select value={taxaResponsavel} label="Taxa fica com" onChange={(e) => setTaxaResponsavel(e.target.value)}>
                          <MenuItem value="clinica">Clínica (vira despesa dedutível)</MenuItem>
                          <MenuItem value="paciente">Paciente</MenuItem>
                        </Select>
                      </FormControl>
                      {taxaSelecionada?.taxaAntecipacaoPercentual != null && (
                        <FormControlLabel
                          sx={{ whiteSpace: 'nowrap' }}
                          control={<Checkbox size="small" checked={antecipado} onChange={(e) => setAntecipado(e.target.checked)} />}
                          label={<Typography variant="caption">Antecipar</Typography>}
                        />
                      )}
                    </Box>
                    {valorTaxaPreview !== null && (
                      <Typography variant="caption" color="text.secondary">
                        Taxa estimada ({percentualPreview}%): {formatarReais(valorTaxaPreview)}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Valor a cobrar */}
        <Typography variant="caption" sx={{ color: '#F97316', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, display: 'block' }}>
          Valor Cobrado Agora
        </Typography>
        <TextField
          value={valor}
          onChange={e => setValor(mascararReais(e.target.value))}
          fullWidth
          size="small"
          inputProps={{ inputMode: 'numeric' }}
          placeholder="R$ 0,00"
          sx={{
            mb: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              fontWeight: 600,
              '&:hover fieldset': { borderColor: 'rgba(249,115,22,0.5)' },
              '&.Mui-focused fieldset': { borderColor: '#F97316' },
            }
          }}
        />
        {valorTotal > 0 && valorNumerico > 0 && (
          <Typography variant="caption" color={saldoAposEstePagamento > 0 ? 'warning.main' : 'success.main'} sx={{ display: 'block', mb: 2 }}>
            {saldoAposEstePagamento > 0
              ? `Pagamento parcial — fica R$ ${formatarReais(saldoAposEstePagamento).replace('R$ ', '')} em aberto.`
              : 'Quita o valor total do orçamento.'}
          </Typography>
        )}

        {/* Observações */}
        <TextField
          placeholder="Observações (opcional)..."
          value={observacoes}
          onChange={e => setObservacoes(e.target.value)}
          fullWidth
          multiline
          rows={2}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              '&.Mui-focused fieldset': { borderColor: '#F97316' },
            }
          }}
        />

      </DialogContent>

      <Divider sx={{ borderColor: 'rgba(249,115,22,0.15)' }} />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary', fontWeight: 600 }}>
          Cancelar
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
          sx={{
            bgcolor: '#F97316',
            '&:hover': { bgcolor: '#EA580C' },
            boxShadow: '0 2px 10px rgba(249,115,22,0.35)',
            borderRadius: '8px', fontWeight: 700,
          }}
        >
          Registrar Pagamento
        </Button>
      </DialogActions>

    </Dialog>
  );
};

export default ModalPagamento;
